import { Component, Renderer2, NgZone } from '@angular/core';
import { MessageService } from 'primeng/api';
import { CommonService } from '../../services/common.service';
import { ToastTimerConfig } from '../../config/toast-timer-config'; // ✅ Import ToastTimerConfig

@Component({
  selector: 'app-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  providers: [MessageService]
})
export class ToastComponent {
  position: string = 'top-right';
  animation = {
    show: 'translateY(100%)',
    hide: 'translateY(-100%)',
    showTime: '1000ms',
    hideTime: '800ms'
  };

  // Track shown errors to prevent duplicates
  private shownErrors: Map<string, number> = new Map();
  // Track API URLs that have failed to suppress their toasts
  private failedApiUrls: Set<string> = new Set();
  // List of API error patterns to always suppress
  private alwaysSuppressPatterns: RegExp[] = [
    /Unauthorized/i,
    /401/,
    /403/,
    /globalSearch/i,
    /api\.qsatel\.com\/api\/orders/i,
    /https:\/\/manage-api\.qsatel\.com\/api\/orders\/globalSearchLog/i,
  ];

  constructor(
    public messageService: MessageService, 
    public commonService: CommonService, 
    private renderer: Renderer2,
    private zone: NgZone
  ) {
    // Subscribe to the toast subject from CommonService
    this.commonService.toastSubject.subscribe((toastData: any) => {
      if (toastData?.elementId) {
        this.showComponentToast(toastData);
      } else {
        // Process the toast through our filtering system
        this.processToast(toastData);
      }
    });

    // Add method to track failed APIs
    this.injectApiErrorTracking();
    
    // Add observer for DOM changes to catch error popups
    this.observeErrorPopups();
  }

  /**
   * Processes incoming toast requests, filtering duplicates and handling API failures
   */
  private processToast(toastData: any) {
    const { type, title, message } = toastData;

    // Always suppress specific API errors that match our patterns
    if (type === 'error' && this.shouldAlwaysSuppressError(title, message)) {
      console.log(`Toast permanently suppressed: ${title} - ${message}`);
      return; // Don't show the toast
    }

    // If this is an error toast
    if (type === 'error') {
      // Check if this is from a failed API that should be suppressed
      if (this.shouldSuppressApiFailure(title, message)) {
        console.log(`Toast suppressed for API failure: ${title} - ${message}`);
        return; // Don't show the toast
      }
      
      // Generate a key to identify this error
      const errorKey = this.generateErrorKey(title, message);
      
      // Check if we've already shown this error recently
      if (this.isDuplicateError(errorKey)) {
        console.log(`Duplicate error suppressed: ${title} - ${message}`);
        return; // Don't show duplicate
      }
      
      // Track this error as shown
      this.trackShownError(errorKey);
    }
    
    // Show the toast if it passed all our filters
    this.showToast(toastData);
  }

  /**
   * Determines if an error should always be suppressed based on specific patterns
   */
  private shouldAlwaysSuppressError(title: string, message: string): boolean {
    // Check specific titles first
    if (title === 'Unity Error' || title === 'Mingle Error') {
      // Check for specific patterns in the message
      return this.alwaysSuppressPatterns.some(pattern => 
        pattern.test(message)
      );
    }
    return false;
  }

  /**
   * Checks if an error toast is a duplicate of a recently shown one
   */
  private isDuplicateError(errorKey: string): boolean {
    return this.shownErrors.has(errorKey);
  }

  /**
   * Tracks an error as shown and sets up automatic cleanup
   */
  private trackShownError(errorKey: string): void {
    // Get current timestamp
    const now = Date.now();
    
    // Store this error with timestamp
    this.shownErrors.set(errorKey, now);
    
    // Set up automatic cleanup after 5 seconds
    setTimeout(() => {
      if (this.shownErrors.get(errorKey) === now) {
        this.shownErrors.delete(errorKey);
      }
    }, 5000);
  }

  /**
   * Generates a unique key for an error based on title and message
   */
  private generateErrorKey(title: string, message: string): string {
    // For Unity/Mingle errors, use a more generic key to prevent duplicates
    if (title === 'Unity Error' || title === 'Mingle Error' || title === 'Error') {
      // Extract the API path if present in the message
      const apiMatch = message.match(/https?:\/\/[^\/]+(\/[^\s:]+)/);
      if (apiMatch && apiMatch[1]) {
        return `${title}-${apiMatch[1]}`;
      }
      
      // Check for status codes
      const statusMatch = message.match(/(401|403|404|500)/);
      if (statusMatch) {
        return `${title}-${statusMatch[1]}`;
      }
    }
    
    // Default key combining title and message
    return `${title}-${message}`;
  }

  /**
   * Observes DOM changes to catch and hide error popups that might not go through toast system
   */
  private observeErrorPopups(): void {
    // Create a MutationObserver to watch for error popups being added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              // Check for error popups by class or content
              this.zone.runOutsideAngular(() => {
                // Look for Unity Error and Mingle Error popups
                const errorPopup = node.querySelector('.unity-error, .mingle-error, div[role="alert"]');
                if (errorPopup) {
                  // Look for specific content
                  const content = errorPopup.textContent || '';
                  if (content.includes('Unity Error') || 
                      content.includes('Mingle Error') ||
                      content.includes('Http failure') ||
                      content.includes('Unauthorized') ||
                      content.includes('api.qsatel.com')) {
                    
                    // Hide the error popup
                    console.log('Hiding error popup via DOM observer');
                    this.renderer.setStyle(node, 'display', 'none');
                    
                    // If it has a close button, click it
                    const closeButton = node.querySelector('button.close, .close-button, [aria-label="Close"]');
                    if (closeButton && closeButton instanceof HTMLElement) {
                      closeButton.click();
                    }
                  }
                }
                
                // Check for error content in any element
                if (node.textContent && (
                    node.textContent.includes('Unity Error') || 
                    node.textContent.includes('Mingle Error') ||
                    node.textContent.includes('401 Unauthorized')
                  )) {
                  // Check if this looks like an error popup
                  if (node.classList.contains('modal') || 
                      node.classList.contains('popup') || 
                      node.classList.contains('toast') ||
                      node.querySelector('.modal-content, .popup-content')) {
                    console.log('Hiding Unity/Mingle error popup');
                    this.renderer.setStyle(node, 'display', 'none');
                  }
                }
              });
            }
          });
        }
      });
    });
    
    // Start observing the document for DOM changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Injects tracking logic into XMLHttpRequest to monitor API failures
   */
  private injectApiErrorTracking(): void {
    const originalOpen = XMLHttpRequest.prototype.open;
    const failedApiUrls = this.failedApiUrls;

    // Override the XHR open method to track URLs
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      // Store the URL on the XHR object for later reference
      this._url = url;
      
      // Add listener for load event
      this.addEventListener('load', function() {
        // Check for error status codes
        if (this.status >= 400) {
          // Mark URL as failed
          if (typeof this._url === 'string') {
            failedApiUrls.add(this._url);
            
            // Automatically clean up after 10 seconds
            setTimeout(() => {
              failedApiUrls.delete(this._url);
            }, 10000);
          }
        } else if (this.status >= 200 && this.status < 300) {
          // Clear URL from failed list if request succeeds (2xx status)
          if (typeof this._url === 'string') {
            failedApiUrls.delete(this._url);
          }
        }
      });
      
      // Add listener for error events
      this.addEventListener('error', function() {
        // Mark URL as failed
        if (typeof this._url === 'string') {
          failedApiUrls.add(this._url);
          
          // Automatically clean up after 10 seconds
          setTimeout(() => {
            failedApiUrls.delete(this._url);
          }, 10000);
        }
      });
      
      return originalOpen.call(this, method, url, ...args);
    };
  }

  /**
   * Checks if a toast should be suppressed due to API failure
   */
  private shouldSuppressApiFailure(title: string, message: string): boolean {
    // Only check error toasts with specific titles
    if (title === 'Unity Error' || title === 'Mingle Error' 
      // || title === 'Error'   , need to fix by rajnish,..... due to umcomment, error toast message is not coming
    ) {
      // Extract API URL from message if present
      const apiMatch = message.match(/https?:\/\/[^\/]+\/[^\s:]+/);
      if (apiMatch) {
        const apiUrl = apiMatch[0];
        
        // Check if this API URL is in our failed list
        return this.failedApiUrls.has(apiUrl);
      }
      
      // Check if message contains common API failure patterns
      return message.includes('failed') || 
             message.includes('error') ||
             message.includes('invalid') ||
             message.includes('unauthorized') ||
             message.includes('400') ||
             message.includes('401');
    }
    
    return false;
  }

  /**
   * Displays a normal PrimeNG toast.
   */
  private showToast(toastData: any) {
    const {
        type,
        title,
        message,
        position,
        sticky = false,
        life = ToastTimerConfig.NORMAL_TOAST_DURATION, // ✅ Get normal toast duration from config
        template = null
    } = toastData;

    // ✅ Map Position to Key
    const positionToKeyMap: { [key: string]: string } = {
        'top-left': 'tl',
        'top-center': 'tc',
        'top-right': 'tr',
        'bottom-left': 'bl',
        'bottom-center': 'bc',
        'bottom-right': 'br'
    };

    const defaultPosition = this.getDefaultPosition(type);
    this.position = position || defaultPosition;
    
    // ✅ Assign mapped key or default to 'kr' (Top Right)
    const toastKey = positionToKeyMap[this.position] || 'kr'; 

    this.animation = this.getAnimationForPosition(this.position);

    const toastId = `toast-${Date.now()}`;

    const toastConfig: any = {
        severity: type,
        summary: title,
        detail: message,
        life: sticky ? undefined : life,
        styleClass: `${this.getToastPositionClass(this.position)} ${toastId} ${sticky ? 'sticky-toast' : ''}`,
        content: template,
        key: toastKey, // ✅ Ensuring key is mapped correctly
        showTransformOptions: this.animation.show,
        showTransitionOptions: this.animation.showTime,
        hideTransitionOptions: this.animation.hideTime,
        hideTransformOptions: this.animation.hide
    };


    this.messageService.add(toastConfig);
    if (!sticky) {
      this.startProgressBar(toastId, life, toastKey);
    }
  }

  /**
   * Attaches a toast badge to a specific component with a blinking effect.
   */
  private showComponentToast(toastData: any) {
    const { type, title, message, elementId } = toastData;
    
    // Check if this is an error that should be suppressed
    if (type === 'error' && (title === 'Unity Error' || title === 'Mingle Error') && 
        this.shouldAlwaysSuppressError(title, message)) {
      console.log(`Component toast suppressed: ${title} - ${message}`);
      return;
    }
    
    const targetElement = document.getElementById(elementId);
    if (!targetElement) return;

    const borderColor = this.getToastColor(type);
    const toastDuration = ToastTimerConfig.BLINKING_TOAST_DURATION; // ✅ Use config for blinking toast duration

    // ✅ Apply Blinking Effect
    this.renderer.setStyle(targetElement, 'border', `2px solid ${borderColor}`);
    
    let blinkCount = 0;
    const totalBlinks = toastDuration / ToastTimerConfig.BLINK_INTERVAL; // ✅ Use config for blink interval

    const interval = setInterval(() => {
      blinkCount++;
      this.renderer.setStyle(targetElement, 'border-color', blinkCount % 2 === 0 ? 'transparent' : borderColor);
      
      if (blinkCount >= totalBlinks) { // ✅ Stops blinking after configured duration
        clearInterval(interval);
        this.renderer.removeStyle(targetElement, 'border');
      }
    }, ToastTimerConfig.BLINK_INTERVAL);

    // ✅ Create Toast Badge
    const toastBadge = this.renderer.createElement('div');
    this.renderer.addClass(toastBadge, 'toast-badge');
    this.renderer.setStyle(toastBadge, 'background', borderColor);
    this.renderer.setProperty(toastBadge, 'innerHTML', `<strong>${title}:</strong> ${message}`);

    // ✅ Position Badge at Top
    this.renderer.setStyle(toastBadge, 'position', 'absolute');
    this.renderer.setStyle(toastBadge, 'top', '-10px');
    this.renderer.setStyle(toastBadge, 'left', '50%');
    this.renderer.setStyle(toastBadge, 'transform', 'translateX(-50%)');
    this.renderer.setStyle(toastBadge, 'padding', '5px 10px');
    this.renderer.setStyle(toastBadge, 'border-radius', '5px');
    this.renderer.setStyle(toastBadge, 'font-size', '12px');
    this.renderer.setStyle(toastBadge, 'color', 'white');
    this.renderer.setStyle(toastBadge, 'white-space', 'nowrap');
    this.renderer.setStyle(toastBadge, 'z-index', '1000');
    this.renderer.setStyle(toastBadge, 'box-shadow', '0px 4px 10px rgba(0, 0, 0, 0.1)');

    // ✅ Attach the badge
    this.renderer.appendChild(targetElement, toastBadge);

    // ✅ Auto-remove Toast & Stop Blinking after configured duration
    setTimeout(() => {
      clearInterval(interval); // ✅ Ensure blinking stops
      this.renderer.removeStyle(targetElement, 'border'); // ✅ Remove border
      this.renderer.removeChild(targetElement, toastBadge); // ✅ Remove toast badge
    }, toastDuration);
  }

  /**
   * Starts progress bar animation and ensures the toast is removed exactly when the progress bar completes.
   */
  private startProgressBar(toastId: string, duration: number, toastKey: string) {
    setTimeout(() => {
        const toastElement = document.querySelector(`.${toastId} .toast-progress-bar`);
        if (toastElement) {
            (toastElement as HTMLElement).style.animation = `progressTimer ${duration}ms linear forwards`;
        }
    }, 100);

    // ✅ Clear the toast **exactly** when the progress bar reaches the end
    setTimeout(() => {
        this.messageService.clear(toastKey);
    }, duration);
  }

  /**
   * Returns the default toast position based on the type.
   */
  private getDefaultPosition(type: string): string {
    const positionDefaults: { [key: string]: string } = {
      success: 'top-right',
      info: 'top-center',
      warn: 'bottom-center',
      error: 'bottom-right'
    };

    return positionDefaults[type] || 'top-right';
  }

  /**
   * Maps position string to PrimeNG toast class.
   */
  getToastPositionClass(position: string): string {
    const positionMap: { [key: string]: string } = {
      'top-left': 'p-toast-top-left',
      'top-center': 'p-toast-top-center',
      'top-right': 'p-toast-top-right',
      'bottom-left': 'p-toast-bottom-left',
      'bottom-center': 'p-toast-bottom-center',
      'bottom-right': 'p-toast-bottom-right'
    };

    return positionMap[position] || 'p-toast-top-right';
  }

  /**
   * Dynamically determines toast show and hide animations based on position.
   */
  private getAnimationForPosition(position: string) {
    const animationMap: { [key: string]: { show: string; hide: string } } = {
      'top-left': { show: 'translateX(-100%)', hide: 'translateX(-100%)' },
      'top-center': { show: 'translateY(-100%)', hide: 'translateY(-100%)' },
      'top-right': { show: 'translateX(100%)', hide: 'translateX(100%)' },
      'bottom-left': { show: 'translateX(-100%)', hide: 'translateX(-100%)' },
      'bottom-center': { show: 'translateY(100%)', hide: 'translateY(100%)' },
      'bottom-right': { show: 'translateX(100%)', hide: 'translateX(100%)' }
    };

    return {
      show: animationMap[position]?.show || 'translateY(100%)',
      hide: animationMap[position]?.hide || 'translateY(100%)',
      showTime: '1000ms',
      hideTime: '800ms'
    };
  }

  /**
   * Returns the correct color based on toast type.
   */
  private getToastColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      success: '#22c55e', // Green
      info: '#6366f1', // Purple
      warn: '#facc15', // Yellow
      error: '#fd0000', // Red
    };
    return colorMap[type] || '#000000'; // Default Black
  }
}
